import { Collection } from '@msw/data'
import { persist } from '@msw/data/extensions/persist'
import type { OrderDetailsExtended } from 'app/src/model'
import { orderSchema } from 'app/src/msw/shopping/schemas/order'
import type { OrderDetails } from 'app/src/services/welfareportal/shopping-api/shopping-schemas'

const discountOrdersCollection = new Collection({
  schema: orderSchema,
  extensions: [persist()],
})

const giftCardsOrdersCollection = new Collection({
  schema: orderSchema,
  extensions: [persist()],
})

const goodAndServiceOrdersCollection = new Collection({
  schema: orderSchema,
  extensions: [persist()],
})

const vouchersOrdersCollection = new Collection({
  schema: orderSchema,
  extensions: [persist()],
})

const refundsOrdersCollection = new Collection({
  schema: orderSchema,
  extensions: [persist()],
})

const transfersOrdersCollection = new Collection({
  schema: orderSchema,
  extensions: [persist()],
})

export {
  discountOrdersCollection,
  giftCardsOrdersCollection,
  goodAndServiceOrdersCollection,
  refundsOrdersCollection,
  transfersOrdersCollection,
  vouchersOrdersCollection,
}

const createDiscountOrdersSeed = async (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
  try {
    console.log('########## MSW: START DISCOUNT ORDERS SEED ##########')

    const discountOrdersExistingData = discountOrdersCollection.findMany()

    console.log('MSW: Checking existing Orders data, found', discountOrdersExistingData.length)

    if (discountOrdersExistingData.length > 0) {
      return
    }

    const discountOrdersJson = await (await fetch('./data/shopping/discountOrders.json')).json()

    await Promise.all(
      discountOrdersJson.items.map(async (item: OrderDetails) => {
        try {
          if (overrides?.firstName && overrides?.lastName && item.userInfo) {
            item.userInfo.firstName = overrides.firstName
            item.userInfo.lastName = overrides.lastName
          }
          if (overrides?.email && item.userInfo) {
            item.userInfo.email = overrides.email
            item.userInfo.userId = overrides.email
          }
          await discountOrdersCollection.create(item)
        } catch (error) {
          console.error('Error creating discount order for id: ', item.orderId, error)
        }
      }),
    )
  } catch (error) {
    console.error('Error seeding discount orders:', error)
  } finally {
    console.log('########## MSW: END DISCOUNT ORDERS SEED ##########')
  }
}

const createGiftCardsOrdersSeed = async (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
  try {
    console.log('########## MSW: START GIFT CARDS ORDERS SEED ##########')
    const giftCardsOrdersExistingData = await giftCardsOrdersCollection.findMany()

    console.log('MSW: Checking existing Orders data, found', giftCardsOrdersExistingData.length)

    if (giftCardsOrdersExistingData.length > 0) {
      return
    }

    const giftCardsOrdersJson = await (await fetch('./data/shopping/giftCardsOrders.json')).json()
    await Promise.all(
      giftCardsOrdersJson.items.map(async (item: OrderDetails) => {
        try {
          if (overrides?.firstName && overrides?.lastName && item.userInfo) {
            item.userInfo.firstName = overrides.firstName
            item.userInfo.lastName = overrides.lastName
          }
          if (overrides?.email && item.userInfo) {
            item.userInfo.email = overrides.email
            item.userInfo.userId = overrides.email
          }
          await giftCardsOrdersCollection.create(item)
        } catch (error) {
          console.error('Error creating gift cards order for id: ', item.orderId, error)
        }
      }),
    )
  } catch (error) {
    console.error('Error seeding gift cards orders:', error)
  } finally {
    console.log('########## MSW: END GIFT CARDS ORDERS SEED ##########')
  }
}

const createGoodAndServiceOrdersSeed = async (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
  try {
    console.log('########## MSW: START GOOD AND SERVICE ORDERS SEED ##########')

    const goodAndServiceOrdersExistingData = goodAndServiceOrdersCollection.findMany()

    console.log('MSW: Checking existing Orders data, found', goodAndServiceOrdersExistingData.length)

    if (goodAndServiceOrdersExistingData.length > 0) {
      return
    }

    const goodAndServiceOrdersJson = await (await fetch('./data/shopping/goodAndServiceOrders.json')).json()

    await Promise.all(
      goodAndServiceOrdersJson.items.map(async (item: OrderDetails) => {
        try {
          if (overrides?.firstName && overrides?.lastName && item.userInfo) {
            item.userInfo.firstName = overrides.firstName
            item.userInfo.lastName = overrides.lastName
          }
          if (overrides?.email && item.userInfo) {
            item.userInfo.email = overrides.email
            item.userInfo.userId = overrides.email
          }
          await goodAndServiceOrdersCollection.create(item)
        } catch (error) {
          console.error('Error creating good and service order for id: ', item.orderId, error)
        }
      }),
    )

    const vouchersOrdersJson = await (await fetch('./data/shopping/vouchersOrders.json')).json()
    await Promise.all(
      vouchersOrdersJson.items.map(async (item: OrderDetails) => {
        try {
          if (overrides?.firstName && overrides?.lastName && item.userInfo) {
            item.userInfo.firstName = overrides.firstName
            item.userInfo.lastName = overrides.lastName
          }
          if (overrides?.email && item.userInfo) {
            item.userInfo.email = overrides.email
            item.userInfo.userId = overrides.email
          }
          await vouchersOrdersCollection.create(item)
        } catch (error) {
          console.error('Error creating voucher order for id: ', item.orderId, error)
        }
      }),
    )
  } catch (error) {
    console.error('Error seeding good and service orders:', error)
  } finally {
    console.log('########## MSW: END GOOD AND SERVICE ORDERS SEED ##########')
  }
}

const createVouchersOrdersSeed = async (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
  try {
    console.log('########## MSW: START VOUCHERS ORDERS SEED ##########')
    const vouchersOrdersExistingData = vouchersOrdersCollection.findMany()

    console.log('MSW: Checking existing Orders data, found', vouchersOrdersExistingData.length)

    if (vouchersOrdersExistingData.length > 0) {
      return
    }

    const vouchersOrdersJson = await (await fetch('./data/shopping/vouchersOrders.json')).json()
    await Promise.all(
      vouchersOrdersJson.items.map(async (item: OrderDetails) => {
        try {
          if (overrides?.firstName && overrides?.lastName && item.userInfo) {
            item.userInfo.firstName = overrides.firstName
            item.userInfo.lastName = overrides.lastName
          }
          if (overrides?.email && item.userInfo) {
            item.userInfo.email = overrides.email
            item.userInfo.userId = overrides.email
          }
          await vouchersOrdersCollection.create(item)
        } catch (error) {
          console.error('Error creating voucher order for id: ', item.orderId, error)
        }
      }),
    )
  } catch (error) {
    console.error('Error seeding vouchers orders:', error)
  } finally {
    console.log('########## MSW: END VOUCHERS ORDERS SEED ##########')
  }
}

const createRefundsOrdersSeed = async (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
  try {
    console.log('########## MSW: START REFUNDS ORDERS SEED ##########')
    const refundsOrdersExistingData = refundsOrdersCollection.findMany()

    console.log('MSW: Checking existing Orders data, found', refundsOrdersExistingData.length)
    if (refundsOrdersExistingData.length > 0) {
      return
    }

    const refundsOrdersJson = await (await fetch('./data/shopping/refundsOrders.json')).json()
    await Promise.all(
      refundsOrdersJson.items.map(async (item: OrderDetails) => {
        try {
          if (overrides?.firstName && overrides?.lastName && item.userInfo) {
            item.userInfo.firstName = overrides.firstName
            item.userInfo.lastName = overrides.lastName
          }
          if (overrides?.email && item.userInfo) {
            item.userInfo.email = overrides.email
            item.userInfo.userId = overrides.email
          }
          await refundsOrdersCollection.create(item)
        } catch (error) {
          console.error('Error creating refund order for id: ', item.orderId, error)
        }
      }),
    )
  } catch (error) {
    console.error('Error seeding refunds orders:', error)
  } finally {
    console.log('########## MSW: END REFUNDS ORDERS SEED ##########')
  }
}

export const createTransferOrderSeed = async (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
  try {
    console.log('########## MSW: START TRANSFERS ORDERS SEED ##########')
    const existingData = transfersOrdersCollection.findMany()

    console.log('MSW: Checking existing transfer data, found', existingData.length)

    if (existingData.length > 0) {
      return
    }

    const jsonData = await (await fetch('./data/shopping/transfersOrders.json')).json()
    await Promise.all(
      jsonData.items.map(async (order: OrderDetailsExtended) => {
        try {
          if (overrides?.firstName && overrides?.lastName && order.userInfo) {
            order.userInfo.firstName = overrides.firstName
            order.userInfo.lastName = overrides.lastName
          }
          if (overrides?.email && order.userInfo) {
            order.userInfo.email = overrides.email
            order.userInfo.userId = overrides.email
          }

          console.log('MSW: Creating transfer with id', order.orderId)

          await transfersOrdersCollection.create(order)
        } catch (error) {
          console.error('Error creating transfer order for id: ', order.orderId, error)
        }
      }),
    )
  } catch (error) {
    console.error('Error seeding transfers orders:', error)
  } finally {
    console.log('########## MSW: END TRANSFERS ORDERS SEED ##########')
  }
}

export const getAllOrders = async (): Promise<OrderDetailsExtended[]> => {
  const discountOrders = discountOrdersCollection.findMany()
  const giftCardsOrders = giftCardsOrdersCollection.findMany()
  const goodAndServiceOrders = goodAndServiceOrdersCollection.findMany()
  const vouchersOrders = vouchersOrdersCollection.findMany()
  const refundsOrders = refundsOrdersCollection.findMany()
  const transfersOrders = transfersOrdersCollection.findMany()

  return [...discountOrders, ...giftCardsOrders, ...goodAndServiceOrders, ...vouchersOrders, ...refundsOrders, ...transfersOrders]
}

export const getOrderById = async (orderId: string) => {
  const allOrders = await getAllOrders()
  return allOrders.find((order) => order.orderId === orderId)
}

export const createOrdersSeeds = async (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
  await createDiscountOrdersSeed(overrides)
  await createGiftCardsOrdersSeed(overrides)
  await createGoodAndServiceOrdersSeed(overrides)
  await createVouchersOrdersSeed(overrides)
  await createRefundsOrdersSeed(overrides)
  await createTransferOrderSeed(overrides)
}

export const clearOrdersSeed = async () => {
  discountOrdersCollection.clear()
  giftCardsOrdersCollection.clear()
  goodAndServiceOrdersCollection.clear()
  refundsOrdersCollection.clear()
  vouchersOrdersCollection.clear()
  transfersOrdersCollection.clear()
}
